import classNames from 'classnames';
import EditMenu from 'components/EditMenu';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import MenuButton from 'components/MenuButton';
import RainbowCardSmall from 'components/RainbowCard/RainbowCardSmall';
import SidebarItem from 'domain/assistant/AssistantSidebar/SidebarItem';
import SidebarItemContent from 'domain/assistant/AssistantSidebar/SidebarItemContent';
import SidebarItemHeader from 'domain/assistant/AssistantSidebar/SidebarItemHeader';
import { useStores } from 'index';
import { observer } from 'mobx-react';
import { AiModuleModelType } from 'models/AiModuleModel';
import { AiModulesStoreType } from 'models/AiModulesStore';
import { HypothesisModelType } from 'models/HypothesisModel';
import { ProjectAiModulesStoreType } from 'models/ProjectAiModulesStore';
import SimplePopup from 'components/SimplePopup';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import AiModulePopup from './AiModulePopup';

interface AiModulesSidebarComponentProps {
  aiModulesStore: AiModulesStoreType;
  projectAiModulesStore: ProjectAiModulesStoreType;
  hypotheses: HypothesisModelType[];
  selectedHypothesisIds?: number[];
  onHypothesisToggle?: (hypothesisId: number, checked: boolean) => void;
  organizationId: number;
  projectId?: number;
  fireStrategy?: string;
  onDownloadPowerpoint?: () => void;
  onDeletePowerpoint?: () => void;
  onCopyToProject?: () => void;
  isPowerpointLoading?: boolean;
  powerpointUrl?: string;
  tasksRunning?: boolean;
}

const SidebarCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  badge?: React.ReactNode;
}> = ({ icon, label, onClick, disabled, badge }) => (
  <RainbowCardSmall shrink gray onClick={onClick} disabled={disabled}>
    <div className="ai-module-card">
      <div className="ai-module-card__top">
        {icon}
        {badge}
      </div>
      <div className="ai-module-card__name">{label}</div>
    </div>
  </RainbowCardSmall>
);

const GeneratingPanel: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
}> = ({ title, subtitle }) => (
  <>
    <div className="ai-modules-sidebar__divider" />
    <div className="ai-modules-sidebar__generating-panel">
      <div className="ai-modules-sidebar__generating-icon">
        <img src="/images/loading.gif" alt="" />
      </div>
      <div className="ai-modules-sidebar__generating-text">
        <span className="ai-modules-sidebar__generating-title">{title}</span>
        {subtitle && (
          <span className="ai-modules-sidebar__generating-subtitle">
            {subtitle}
          </span>
        )}
      </div>
    </div>
  </>
);

const AiModulesSidebarComponent: React.FC<AiModulesSidebarComponentProps> =
  observer(
    ({
      aiModulesStore,
      projectAiModulesStore,
      hypotheses,
      selectedHypothesisIds,
      onHypothesisToggle,
      organizationId,
      projectId,
      fireStrategy,
      onDownloadPowerpoint,
      onDeletePowerpoint,
      onCopyToProject,
      isPowerpointLoading,
      powerpointUrl,
      tasksRunning
    }) => {
      const intl = useIntl();
      const [selectedModule, setSelectedModule] =
        useState<AiModuleModelType | null>(null);
      const [isOverride, setIsOverride] = useState(false);
      const [showTasksRunningPopup, setShowTasksRunningPopup] = useState(false);
      const [showCopyConfirmPopup, setShowCopyConfirmPopup] = useState(false);
      const [activeTab, setActiveTab] = useState<'studio' | 'progress'>(
        'studio'
      );

      const { dataStore } = useStores();

      const inProgressRequests =
        dataStore?.gptRequestCounterList?.filter(
          (item) => item.status === 'in_progress'
        ) || [];
      const completedRequests =
        dataStore?.gptRequestCounterList?.filter(
          (item) => item.status === 'completed'
        ) || [];
      const failedRequests =
        dataStore?.gptRequestCounterList?.filter(
          (item) => item.status === 'failed'
        ) || [];

      const totalCurrentAmount = inProgressRequests.reduce(
        (sum, item) => sum + (item.current_amount || 0),
        0
      );
      const totalTargetAmount = inProgressRequests.reduce(
        (sum, item) => sum + (item.target_amount || 0),
        0
      );

      const isGenerating = projectAiModulesStore.isAnyModuleGenerating;
      const generatingModules = projectAiModulesStore.generatingModules;
      const hasActiveRequests =
        inProgressRequests.length > 0 || isGenerating || !!isPowerpointLoading;

      const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

      useEffect(() => {
        aiModulesStore.getAiModules();
      }, [aiModulesStore]);

      const modules = aiModulesStore.aiModuleListForStrategy(fireStrategy);

      return (
        <div className="fire-sidebar">
          <div className="ai-modules-sidebar__tabs">
            <button
              className={classNames('ai-modules-sidebar__tab', {
                'ai-modules-sidebar__tab--active': activeTab === 'studio'
              })}
              onClick={() => setActiveTab('studio')}
            >
              <Icon name="stars" />
              <span>
                <FormattedMessage id="AI Studio" />
              </span>
            </button>
            <button
              className={classNames('ai-modules-sidebar__tab', {
                'ai-modules-sidebar__tab--active': activeTab === 'progress'
              })}
              onClick={() => setActiveTab('progress')}
            >
              <Icon name="ai-studio" />
              <span>
                {inProgressRequests.length > 0 ? (
                  <FormattedMessage
                    id="x of y items"
                    values={{
                      x: totalCurrentAmount,
                      y: totalTargetAmount
                    }}
                  />
                ) : hasActiveRequests ? (
                  <FormattedMessage id="Generating ..." />
                ) : (
                  <FormattedMessage id="No task" />
                )}
              </span>
            </button>
          </div>

          {activeTab === 'studio' && (
            <>
              {aiModulesStore.isListLoading && <Loading />}

              {!aiModulesStore.isListLoading && (
                <SidebarItem>
                  <SidebarItemHeader opaque>
                    <FormattedMessage id="AI Studio description" />
                  </SidebarItemHeader>
                  <SidebarItemContent>
                    <div className="ai-modules-sidebar__grid">
                      {modules.map((aiModule) => {
                        const existing = projectAiModulesStore.findByAiModuleId(
                          aiModule.id
                        );
                        const isDone =
                          !!existing &&
                          (existing.status === 'done' ||
                            (!existing.status && !!existing.output));
                        const isModuleGenerating =
                          !!existing &&
                          (existing.status === 'created' ||
                            existing.status === 'running');
                        const isFailed =
                          !!existing && existing.status === 'failed';
                        return (
                          <SidebarCard
                            key={aiModule.id}
                            icon={
                              aiModule.icon_url ? (
                                <img
                                  src={aiModule.icon_url}
                                  alt={aiModule.name || ''}
                                  className="ai-module-card__icon"
                                />
                              ) : (
                                <Icon name="ai" />
                              )
                            }
                            label={aiModule.name || ''}
                            disabled={isDone || isModuleGenerating}
                            badge={
                              isDone ? (
                                <div className="ai-module-card__check">
                                  <Icon name="check" />
                                </div>
                              ) : (
                                <div className="ai-module-card__check">
                                  <Icon name="stars" />
                                </div>
                              )
                            }
                            onClick={() => {
                              if (isModuleGenerating) {
                                return;
                              }
                              if (tasksRunning) {
                                setShowTasksRunningPopup(true);
                                return;
                              }
                              setIsOverride(isDone || isFailed);
                              setSelectedModule(aiModule);
                            }}
                          />
                        );
                      })}

                      {onDownloadPowerpoint && (
                        <SidebarCard
                          icon={<Icon name="powerpoint" />}
                          label="Powerpoint Export"
                          disabled={!!powerpointUrl}
                          badge={
                            powerpointUrl ? (
                              <div className="ai-module-card__check">
                                <Icon name="check" />
                              </div>
                            ) : (
                              <div className="ai-module-card__check">
                                <Icon name="stars" />
                              </div>
                            )
                          }
                          onClick={
                            tasksRunning
                              ? () => setShowTasksRunningPopup(true)
                              : isPowerpointLoading
                                ? undefined
                                : powerpointUrl
                                  ? undefined
                                  : onDownloadPowerpoint
                          }
                        />
                      )}

                      {onCopyToProject && (
                        <SidebarCard
                          icon={<Icon name="convert" />}
                          label={intl.formatMessage({
                            id: 'Copy to Project'
                          })}
                          onClick={
                            tasksRunning
                              ? () => setShowTasksRunningPopup(true)
                              : () => setShowCopyConfirmPopup(true)
                          }
                          badge={
                            <div className="ai-module-card__check">
                              <Icon name="stars" />
                            </div>
                          }
                        />
                      )}
                    </div>
                  </SidebarItemContent>
                </SidebarItem>
              )}

              {powerpointUrl && (
                <>
                  <div className="ai-modules-sidebar__divider" />
                  <SidebarItemHeader opaque>
                    <FormattedMessage id="Powerpoint download teaser" />
                  </SidebarItemHeader>
                  <div className="ai-modules-sidebar__download">
                    <a
                      href={powerpointUrl}
                      download
                      className="ai-modules-sidebar__download-link"
                    >
                      <div className="ai-modules-sidebar__download-icon">
                        <Icon name="powerpoint" />
                      </div>
                      <div className="ai-modules-sidebar__download-text">
                        <span className="ai-modules-sidebar__download-title">
                          <FormattedMessage id="Presentation" />
                        </span>
                        <span className="ai-modules-sidebar__download-subtitle">
                          <FormattedMessage id="Download here" />
                        </span>
                      </div>
                    </a>
                    <EditMenu
                      label={intl.formatMessage({ id: 'Actions' })}
                      topRight
                    >
                      <MenuButton
                        label={<FormattedMessage id="Regenerate PPTX" />}
                        iconName="update"
                        onClick={() => onDownloadPowerpoint?.()}
                      />
                      <MenuButton
                        label={<FormattedMessage id="Delete PPTX" />}
                        iconName="bin"
                        onClick={() => onDeletePowerpoint?.()}
                      />
                    </EditMenu>
                  </div>
                </>
              )}

              {generatingModules.map((pam) => (
                <GeneratingPanel
                  key={pam.id}
                  title={
                    <FormattedMessage
                      id="Generating {moduleName} ..."
                      values={{ moduleName: pam.ai_module?.name || '' }}
                    />
                  }
                />
              ))}

              {isPowerpointLoading && (
                <GeneratingPanel
                  title={<FormattedMessage id="Generating Powerpoint ..." />}
                />
              )}
            </>
          )}

          {activeTab === 'progress' && (
            <div className="ai-modules-sidebar__progress">
              <div className="ai-modules-sidebar__progress-section">
                <div className="ai-modules-sidebar__progress-heading">
                  <FormattedMessage id="GPTRequest active" />
                </div>
                {inProgressRequests.length === 0 &&
                !isGenerating &&
                !isPowerpointLoading ? (
                  <p className="ai-modules-sidebar__progress-empty">
                    <FormattedMessage id="None" />
                  </p>
                ) : (
                  <>
                    {inProgressRequests.map((item) => (
                      <p
                        className="ai-modules-sidebar__progress-item"
                        key={item.id}
                      >
                        <FormattedMessage
                          id="x of y"
                          values={{
                            x: item.current_amount,
                            y: item.target_amount
                          }}
                        />{' '}
                        {capitalize(item.request_type || '')}
                      </p>
                    ))}
                    {generatingModules.map((pam) => (
                      <p
                        className="ai-modules-sidebar__progress-item"
                        key={pam.id}
                      >
                        <FormattedMessage
                          id="Generating {moduleName} ..."
                          values={{
                            moduleName: pam.ai_module?.name || ''
                          }}
                        />
                      </p>
                    ))}
                    {isPowerpointLoading && (
                      <p className="ai-modules-sidebar__progress-item">
                        <FormattedMessage id="Generating Powerpoint ..." />
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="ai-modules-sidebar__divider" />

              <div className="ai-modules-sidebar__progress-section">
                <div className="ai-modules-sidebar__progress-heading">
                  <FormattedMessage id="GPTRequest completed" />
                </div>
                {completedRequests.length === 0 ? (
                  <p className="ai-modules-sidebar__progress-empty">
                    <FormattedMessage id="None" />
                  </p>
                ) : (
                  completedRequests.map((item) => (
                    <p
                      className="ai-modules-sidebar__progress-item"
                      key={item.id}
                    >
                      <FormattedMessage
                        id="x of y"
                        values={{
                          x: item.current_amount,
                          y: item.target_amount
                        }}
                      />{' '}
                      {capitalize(item.request_type || '')}
                    </p>
                  ))
                )}
              </div>

              <div className="ai-modules-sidebar__divider" />

              <div className="ai-modules-sidebar__progress-section">
                <div className="ai-modules-sidebar__progress-heading">
                  <FormattedMessage id="GPTRequest failed" />
                </div>
                {failedRequests.length === 0 ? (
                  <p className="ai-modules-sidebar__progress-empty">
                    <FormattedMessage id="None" />
                  </p>
                ) : (
                  failedRequests.map((item) => (
                    <p
                      className="ai-modules-sidebar__progress-item"
                      key={item.id}
                    >
                      <FormattedMessage
                        id="x of y"
                        values={{
                          x: item.current_amount,
                          y: item.target_amount
                        }}
                      />{' '}
                      {capitalize(item.request_type || '')}
                    </p>
                  ))
                )}
              </div>
            </div>
          )}

          {selectedModule && projectId && (
            <AiModulePopup
              aiModule={selectedModule}
              hypotheses={hypotheses}
              preselectedHypothesisIds={selectedHypothesisIds}
              organizationId={organizationId}
              projectId={projectId}
              projectAiModulesStore={projectAiModulesStore}
              isOverride={isOverride}
              onClose={() => setSelectedModule(null)}
              onHypothesisToggle={onHypothesisToggle}
            />
          )}

          {showCopyConfirmPopup &&
            createPortal(
              <SimplePopup
                headline={
                  <span className="ai-modules-sidebar__popup-headline">
                    <Icon name="arrow-right" size="medium" />
                    <FormattedMessage id="Copy to project title" />
                  </span>
                }
                onAbort={() => setShowCopyConfirmPopup(false)}
                onSubmit={() => {
                  setShowCopyConfirmPopup(false);
                  onCopyToProject?.();
                }}
                submitTextId="Confirm"
                front
              >
                <p>
                  <FormattedMessage id="Copy to project message" />
                </p>
              </SimplePopup>,
              document.body
            )}

          {showTasksRunningPopup &&
            createPortal(
              <SimplePopup
                headline={
                  <span className="ai-modules-sidebar__popup-headline">
                    <Icon name="ai" size="medium" />
                    <FormattedMessage id="AI Studio tasks running title" />
                  </span>
                }
                onAbort={() => setShowTasksRunningPopup(false)}
                front
              >
                <p>
                  <FormattedMessage id="AI Studio tasks running message" />
                </p>
              </SimplePopup>,
              document.body
            )}
        </div>
      );
    }
  );

export default AiModulesSidebarComponent;
